/**
 * The Printer Helper's "choose a printer" step on a desktop register.
 *
 * Mobile shows an inline SDK scan list there. A desktop register instead gets
 * one button into the same Add Printer flow Settings uses (`LanPrinterDialog`),
 * so Electron has a single way to add a printer: discovery or IP address →
 * check → test page → save.
 */
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MuiPrintIcon from '@mui/icons-material/Print';

import { useBoolean } from 'src/hooks/use-boolean';

import { usePrinterStore } from 'src/store/PrinterStore';
import type { SavedPrinter } from 'src/services/printers';
import { getPlatformPrinter } from 'src/platform/services';
import { checkPrinterReadiness } from 'src/services/printing';

import MuiIcon from 'src/components/mui-icon';

import LanPrinterDialog from './lan-printer-dialog';
import EscPosPrinterDialog from '../escpos/escpos-printer-dialog';

const escPosAvailable = getPlatformPrinter().escPosLanPrinting;

interface DesktopAddPrinterEntryProps {
  /** Called after a printer is saved and set active. */
  onConnected?: () => void;
}

export default function DesktopAddPrinterEntry({ onConnected }: DesktopAddPrinterEntryProps) {
  const dialog = useBoolean(false);
  const activeId = usePrinterStore((state) => state.activePrinter?.identifier ?? null);
  const method = usePrinterStore((state) => state.printerMethod);
  const escpos = escPosAvailable && method === 'escpos';

  const handleSaved = async (printer: SavedPrinter) => {
    await usePrinterStore.getState().setActivePrinter(printer);
    await checkPrinterReadiness({ force: true });
    onConnected?.();
  };

  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {escpos
          ? 'Scan this shop’s network for an ESC/POS printer, or add it by its IP address and port. It is checked and prints a test page before it is saved.'
          : 'Find the Star printer on this shop’s network, or add it by its IP address. It is checked and prints a test page before it is saved.'}
      </Typography>
      <Button
        variant="contained"
        onClick={dialog.onTrue}
        startIcon={<MuiIcon icon={MuiPrintIcon} />}
      >
        {escpos ? 'Add ESC/POS Printer' : 'Add Printer'}
      </Button>
      {escpos ? (
        <EscPosPrinterDialog
          open={dialog.value}
          onClose={dialog.onFalse}
          currentDefaultId={activeId}
          onSaved={(printer, use) => {
            if (use) handleSaved(printer);
            else usePrinterStore.getState().savePrinter(printer);
          }}
        />
      ) : (
        <LanPrinterDialog
          open={dialog.value}
          onClose={dialog.onFalse}
          onSelected={handleSaved}
          currentDefaultId={activeId}
        />
      )}
    </Stack>
  );
}
